import React from 'react';
import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toCapitalized } from '../../Utils/Capitalize';

const StatusBadge = ({
  status = 'default', // 'pending' | 'approved' | 'success' | 'info' | 'danger' | 'default'
  label = '',
  type = '',
  color = null, // Direct color prop for maximum flexibility
  variant = 'outlined', // 'filled' | 'outlined' | 'soft' (default to soft as per design)
  size = 'small', // 'small' | 'medium' | 'large'
  showIcon = false,
  icon = null,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') {
      return `rgba(0, 0, 0, ${alpha})`;
    }
  
    hex = hex.replace('#', '');
    
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };


  const getLabelBasedColor = (labelText) => {
    if (!labelText && labelText !== 0) return null;
    
    const stringLabel = String(labelText);
    const normalizedLabel = stringLabel.toLowerCase().replace(/\s+/g, '').trim();
    
    const pendingPatterns = [
      'pending', 'inprogress', 'progress', 'waiting', 'scheduled', 'processing',
      'review','PENDING'
    ];
    
    const approved = [
      'announced', 'new', 'approved', 
    ];
    // Info category patterns
    const infoPatterns = [
      'info', 'information', 'new', 'latest', 'updated', 'available',
      'inshop', 'shop', 'store', 'featured', 'highlight', 'notice', 'partially',
      'announcement', 'beta', 'preview', 'draft', 'staging', 'partially_completed'
    ];
    // Danger/Error category patterns
    const dangerPatterns = [
      'danger', 'error', 'failed', 'failure', 'rejected', 'denied', 
      'cancelled', 'canceled', 'inactive', 'disabled', 'blocked',
      'suspended', 'expired', 'overdue', 'urgent', 'critical',
      'notverified' ,'not_verified', 'unverified', 'invalid', 'unauthorized', 'forbidden',
      'dormant'
    ];
    const successPatterns = [
      'active', 'completed', 'done', 'finished', 'success',
      'verified', 'confirmed','gifted', 'published', 'live', 'online', 'enabled',
      'paid', 'delivered', 'shipped', 'fulfilled', 'accepted', 'validated',
      'authorized', 'certified', 'cleared', 'resolved', 'fixed', 'popular'
    ];
    
    // Check patterns and return appropriate color
    // Check danger patterns first to handle cases like "not_verified" before "verified"
    if (dangerPatterns.some(pattern => normalizedLabel.includes(pattern))) {
      return '#CC0000'; // Danger
    }
    if (pendingPatterns.some(pattern => normalizedLabel.includes(pattern))) {
      return '#FFBF40'; // Pending/Warning
    }
    if( approved.some(pattern => normalizedLabel.includes(pattern))) {
      return '#995A00'; // Approved/New as Info
    }
    // Check info patterns before success to handle cases like "partially_completed" before "completed"
    if (infoPatterns.some(pattern => normalizedLabel.includes(pattern))) {
      return '#0081F2'; // Info
    }
    if (successPatterns.some(pattern => normalizedLabel.includes(pattern))) {
      return '#008000'; // Success
    }
    
    return null; // No pattern matched
  };

  // Get the actual color to use with intelligent fallbacks
  const getColor = () => {
    if (color) return color;

    const labelColor = getLabelBasedColor(label);
    if (labelColor) return labelColor;
  };

  const actualColor = getColor();
  const displayLabel = (label || status.charAt(0).toUpperCase() + status.slice(1)).replace(/_/g, ' ');

  // Default icons for each category
  const defaultIcons = {
    pending: '◐', warning: '⚠', scheduled: '📅', waiting: '⏳', inProgress: '◐', announced: '📢',
    success: '✓', approved: '✓', active: '●', verified: '✓', completed: '✓', published: '✓', 
    gifted: '🎁', redeemed: '🎯',
    info: 'ℹ', new: '✨', inShop: '🏪',
    danger: '✕', error: '✕', rejected: '✕', failed: '✕', cancelled: '✕', 
    inactive: '○', notVerified: '✕',
    default: '○', draft: '○'
  };

  const displayIcon = icon || (showIcon ? defaultIcons[status] || defaultIcons.default : null);

  // Size configurations
  const sizeConfigs = {
    small: {
      fontSize: '11px',
      lineHeight: '20px',
      padding: '4px 8px',
      iconSize: '0.7rem'
    },
    medium: {
      fontSize: '13px',
      lineHeight: '24px',
      padding: '4px 16px',
      iconSize: '0.8rem'
    },
    large: {
      fontSize: '16px',
      lineHeight: '24px',
      padding: '4px 8px',
      iconSize: '1rem'
    }
  };

  const sizeConfig = sizeConfigs[size] || sizeConfigs.medium;
  
  const getVariantStyles = () => {
    switch (variant) {
      case 'outlined':
        return {
          backgroundColor: hexToRgba(actualColor, 0.12),
          color: theme.palette.text.primary,
          outline: `1px solid ${actualColor}`,
          outlineOffset: '-1px',
        };
      case 'filled':
        return {
          backgroundColor: actualColor,
          color: theme.palette.common.white,
          outline: `1px solid ${actualColor}`,
          outlineOffset: '-1px',
        };
      case 'text': 
        return {
          backgroundColor: 'transparent',
          color: actualColor,
          outline: 'none',
          outlineOffset: '-1px',
        };
      case 'soft':
      default:
        return {
          backgroundColor: hexToRgba(actualColor, 0.12),
          color: actualColor,
          outline: 'none',
          outlineOffset: '-1px',
        };
    }
  };

  const chipStyles = {
    fontSize: sizeConfig.fontSize,
    fontWeight: 600,
    lineHeight: sizeConfig.lineHeight,
    padding: size === 'small' ? '2px 4px' : '4px 8px',
    borderRadius: type = 'disclaimer' ?  '8px' : '16px',
    height: size === 'small' ? '24px' : '28px',
    '& .MuiChip-label': {
      paddingLeft: size === 'small' ? '4px' : '8px',
      paddingRight: size === 'small' ? '4px' : '8px',
    },
    ...getVariantStyles(),
    ...sx,
  };

  return (
    <Chip
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: displayIcon ? 0.5 : 0,
            height: '100%',
          }}
        >
          {displayIcon && (
            <Box
              component="span"
              sx={{
                fontSize: sizeConfig.iconSize,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {displayIcon}
            </Box>
          )}
          <Box component="span">{toCapitalized(displayLabel)}</Box>
        </Box>
      }
      sx={chipStyles}
      {...props}
    />
  );
};

export default StatusBadge;